/*
 * TruCustom cart enhancement — storefront runtime.
 *
 * The product page proves a design was saved; this does the same job one step
 * later, in the cart. Without it a customized line is indistinguishable from a
 * plain one except for a raw preview URL printed as text, which reads like a
 * bug rather than a confirmation.
 *
 * Same two rules as trucustom.js:
 *   1. Degrade to nothing. The cart is the last step before checkout — a script
 *      error here is worse than no decoration at all.
 *   2. Never decide the property contract locally. Designs::LineItemProperties
 *      is the single source of truth for what keys exist; this file just reads
 *      them.
 *
 * Reads the visible "Design Preview"/"Design Artwork"/"Second Side
 * Preview"/"Second Side Artwork" keys (2026-09-23 — previously read
 * `_tc_preview_url`/`_tc_artwork_url`/`_tc_second_preview_url`/
 * `_tc_second_artwork_url`, which existed ONLY to give this file a name that
 * wouldn't depend on the human-facing label; they were removed as pure
 * duplication of the same value under a second key). `_tc_design_token` stays
 * the presence check — it has no visible counterpart — and the old preview/
 * artwork keys are still read as a fallback so a cart line added in the
 * moments right before this deployed keeps decorating correctly rather than
 * losing its badge.
 */
(function () {
  'use strict';

  // Bail if another copy of this file already ran. TruCustom can reach a
  // storefront two ways — as the Shopify app's theme extension, or as theme code
  // pasted in while app review is pending — and a store mid-migration can have
  // both. Two copies would decorate the same cart lines twice.
  if (window.__TRUCUSTOM_CART__) return;
  window.__TRUCUSTOM_CART__ = true;

  var PREFIX = '[TruCustom cart]';
  var CARD_CLASS = 'trucustom-cart__design';
  var PREVIEW_ATTR = 'data-trucustom-preview';
  var NOTE_CLASS = 'trucustom-cart__note';

  // Where a theme puts its "Check out" control. `[name="checkout"]` is the
  // Shopify convention — the cart form's submit — and covers Dawn, Horizon and
  // most OS 2.0 themes. The rest are fallbacks for themes that link straight to
  // /checkout instead of submitting the form.
  var CHECKOUT_SELECTORS = [
    '[name="checkout"]',
    'button[data-checkout], a[data-checkout]',
    'a[href="/checkout"], a[href$="/checkout"]',
    '.cart__checkout-button, .cart__checkout'
  ];

  // A print-type surcharge line must never be removed or re-quantified on
  // its own — it isn't a product the customer chose, it's the price of one
  // they already did, and its quantity is kept in sync with that product by
  // syncSurchargeQuantities below. These cover the same remove-button/
  // quantity-stepper conventions CHECKOUT_SELECTORS does for Dawn/Horizon/
  // most OS 2.0 themes; a theme using neither is a silent no-op, same as
  // everywhere else in this file.
  var REMOVE_SELECTORS = [
    'cart-remove-button', // Dawn's custom element wrapping the remove control
    '.cart-items__remove', // Horizon
    'button[name="remove"], a[name="remove"]'
  ];
  var QUANTITY_SELECTORS = [
    'input[type="number"]',
    'button[name="minus"], button[name="plus"]'
  ];

  // How much text an element may hold and still count as "just this property".
  // "Design Preview: " plus a little theme punctuation.
  var LINE_SLACK = 40;

  // Floor on /cart.js traffic. The observer below fires on unrelated theme
  // churn too (lazy images, sticky headers), so unthrottled it would poll.
  var MIN_FETCH_INTERVAL = 1500;

  // Longest run of text still treated as a property label rather than content.
  var LABEL_MAX = 32;

  function warn(message, error) {
    try { console.warn(PREFIX + ' ' + message, error === undefined ? '' : error); } catch (e) {}
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function boot() {
    var root = document.querySelector('[data-trucustom-cart]');
    if (!root) return;

    try {
      var ctx = buildContext(root);
      refresh(ctx);
      watch(ctx);
    } catch (error) {
      warn('Failed to start; leaving the cart untouched.', error);
    }
  }

  function buildContext(root) {
    var data = root.dataset;

    return {
      badgeLabel: data.badgeLabel || 'Customized',
      viewLabel: data.viewLabel || 'View design',
      // What the customer is told about print fidelity before they check out.
      // A blank setting turns the notice off entirely, which is why this reads
      // the attribute rather than defaulting a missing-vs-empty value the same
      // way: `data-print-note=""` means "no thanks".
      printNote: data.printNote === undefined
        ? 'Our team of experts will print your design to the best of their ability, matching your mockup as closely as possible.'
        : data.printNote,
      thumbAlt: data.thumbAlt || 'Your customized design',
      showThumbnail: data.showThumbnail !== 'false',

      items: [],
      lastFetch: 0,
      busy: false,
      warned: false,
      lightbox: null,
      // Cart line keys with an in-flight /cart/change.js request from
      // syncSurchargeQuantities — see that function for why.
      syncingLines: {},
      // A tick() throttled by MIN_FETCH_INTERVAL that still needs a trailing
      // refresh once the window reopens — see tick()'s own comment.
      trailingTick: null
    };
  }

  // ── Reading the cart ──────────────────────────────────────────────────────

  // /cart.js returns underscore-prefixed properties too — they are hidden from
  // rendered UI, not from the API. `_tc_design_token` is the presence check:
  // it has no visible counterpart, so it's the one property here guaranteed
  // to exist under exactly this name.
  //
  // Preview/artwork read the visible keys first, with the retired hidden keys
  // as a fallback — see the file header. A design predating front-and-back
  // printing (or single-sided) simply has no second-side properties at all,
  // which is why those fall back to null rather than the primary values.
  function customizedLines(cart) {
    var items = (cart && cart.items) || [];
    var out = [];

    for (var i = 0; i < items.length; i++) {
      var properties = items[i].properties || {};
      if (!properties._tc_design_token) continue;

      var preview = properties['Design Preview'] || properties._tc_preview_url;
      if (!preview) continue;

      out.push({
        preview: preview,
        artwork: properties['Design Artwork'] || properties._tc_artwork_url || null,
        secondPreview: properties['Second Side Preview'] || properties._tc_second_preview_url || null,
        secondArtwork: properties['Second Side Artwork'] || properties._tc_second_artwork_url || null,
        primarySide: properties._tc_primary_side || null,
        secondSide: properties._tc_second_side || null,
        // Used by syncSurchargeQuantities below, not by decoration.
        designToken: properties._tc_design_token,
        quantity: items[i].quantity
      });
    }
    return out;
  }

  // Every cart line that's a print-type surcharge — see trucustom.js's
  // surchargeCartItem(), which is what sets `_tc_linked_design`. Line `key`
  // (not `id`/variant id, which several lines can share) is what
  // /cart/change.js needs to target exactly this one.
  function surchargeLines(cart) {
    var items = (cart && cart.items) || [];
    var out = [];

    for (var i = 0; i < items.length; i++) {
      var properties = items[i].properties || {};
      if (!properties._tc_linked_design) continue;

      out.push({
        designToken: properties._tc_linked_design,
        key: items[i].key,
        quantity: items[i].quantity
      });
    }
    return out;
  }

  // Keeps each surcharge line's quantity matching its paired product line —
  // see trucustom.js's surchargeCartItem() for how they're linked, and the
  // comment on syncingLines for why this needs no new global interception:
  // ANY way the customer changed the cart (the theme's own quantity stepper,
  // a cart drawer, a bulk /cart/update.js call, even a full page reload) ends
  // up here, since it's this exact /cart.js snapshot that already drives the
  // badge decoration above. Diffing against the PREVIOUS snapshot (ctx.items,
  // the badge mechanism's own cache) would only catch the design's own
  // metadata changing; comparing straight against the current cart's own
  // surcharge lines is simpler AND correct without needing a previous state.
  //
  // A design whose primary line has disappeared (removed from the cart) has
  // no entry in `primaries` — the ternary's `0` for that case is what
  // removes the now-orphaned surcharge line too, since /cart/change.js
  // treats quantity 0 as "remove this line".
  function syncSurchargeQuantities(ctx, cart) {
    var primaries = {};
    ctx.items.forEach(function (item) {
      if (item.designToken) primaries[item.designToken] = item.quantity;
    });

    surchargeLines(cart).forEach(function (surcharge) {
      var wanted = primaries[surcharge.designToken];
      var target = wanted === undefined ? 0 : wanted;
      if (target !== surcharge.quantity) changeLineQuantity(ctx, surcharge.key, target);
    });
  }

  // One in-flight request per line at a time (syncingLines), not per tick —
  // this file's own polling can tick again before a slow /cart/change.js
  // response lands, and firing a second request for the same line while the
  // first is still in flight risks the two racing to opposite outcomes.
  // Released once the request settles either way, so a still-wrong quantity
  // (customer kept clicking while this was in flight) gets corrected on the
  // next tick rather than being silently dropped.
  function changeLineQuantity(ctx, lineKey, quantity) {
    if (ctx.syncingLines[lineKey]) return;
    ctx.syncingLines[lineKey] = true;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) {
        // Ours, from this file — tells our own badge/note decoration (see
        // watch()) to re-check itself; it does NOT reach the theme's own
        // drawer/cart-items-component, which is what left the on-screen
        // total stale after this write (reported alongside this fix).
        try { document.dispatchEvent(new CustomEvent('trucustom:cart-updated')); } catch (e) {}
        if (cart) notifyThemeCartUpdated(cart);
      })
      .catch(function (error) {
        warn('Could not keep the print-type surcharge quantity in sync.', error);
      })
      .then(function () { delete ctx.syncingLines[lineKey]; });
  }

  // Tells the THEME its own drawer/cart page should re-render — the same
  // shopify:cart:lines-update Standard Event trucustom.js's own
  // notifyThemeCartUpdated dispatches after an add-to-cart (see its longer
  // comment for the full picture). A separate copy because these are two
  // independent script files with no shared runtime to call into each other
  // through.
  //
  // `lines: []` deliberately: this is reporting an 'update', and every
  // listener here only actually reads event.promise, never event.lines.
  function notifyThemeCartUpdated(cart) {
    try { dispatchCartLinesUpdate(cart, 'update', 'cart', []); }
    catch (error) { warn('Could not refresh the theme cart UI.', error); }
  }

  // The event is built by hand rather than with Shopify's own
  // CartLinesUpdateEvent from standard-events.js, and this must NOT be
  // "simplified" back to a dynamic import. This is a classic (non-module)
  // script, and Shopify's theme asset pipeline rewrites a dynamic import in
  // those into a CommonJS require call, which doesn't exist in a browser —
  // so every refresh threw "require is not defined" on live stores while
  // working fine from unminified local source. The theme only reads the
  // properties set on the event itself (action, context, lines, promise),
  // and the promise resolves to {cart, detail}; the theme's own
  // pricing-tiers-cart-reconcile.js dispatches it the same way for the same
  // reason. Same copy in trucustom.js — no shared runtime between the two files.
  function dispatchCartLinesUpdate(cart, action, context, lines) {
    var event = new Event('shopify:cart:lines-update', { bubbles: true, cancelable: false });
    event.action = action;
    event.context = context;
    event.lines = lines;
    event.promise = Promise.resolve({
      cart: cartSummary(cart),
      detail: { items: cart.items, source: 'trucustom', didError: false }
    });
    document.dispatchEvent(event);
  }

  // The Storefront-API-shaped cart summary the event's promise carries,
  // built from an Ajax API /cart.js response (prices there are in cents).
  function cartSummary(cart) {
    var currency = cart.currency || '';
    return {
      id: cart.token,
      totalQuantity: cart.item_count,
      cost: { totalAmount: { amount: (Number(cart.total_price) / 100).toFixed(2), currencyCode: currency } },
      lines: (cart.items || []).map(function (item) {
        return {
          id: item.key,
          quantity: item.quantity,
          cost: { totalAmount: { amount: (Number(item.final_line_price) / 100).toFixed(2), currencyCode: currency } }
        };
      }),
      discountCodes: (cart.discount_codes || []).map(function (d) { return { applicable: !!d.applicable, code: d.code }; })
    };
  }

  // Finds a cart line's row by its key. `id="CartItem-<key>"` is a
  // long-standing Dawn/OS 2.0 convention; `[data-key="<key>"]` is Horizon's.
  // Returns null for a theme using neither — callers treat that as "nothing
  // to lock down here", not an error.
  function cartLineRow(key) {
    if (!key) return null;

    var escaped = (window.CSS && CSS.escape) ? CSS.escape(key) : null;
    return document.getElementById('CartItem-' + key) ||
      (escaped && document.querySelector('[data-key="' + escaped + '"]'));
  }

  // Hides the remove control and disables the quantity stepper/input on every
  // surcharge line's row — see the comment on REMOVE_SELECTORS/
  // QUANTITY_SELECTORS above for why. Re-run every refresh tick; both
  // `hidden`/`disabled` are idempotent to set repeatedly.
  function lockSurchargeRows(cart) {
    surchargeLines(cart).forEach(function (surcharge) {
      var row = cartLineRow(surcharge.key);
      if (!row) return;

      REMOVE_SELECTORS.forEach(function (selector) {
        var matches;
        try { matches = row.querySelectorAll(selector); } catch (e) { return; }
        matches.forEach(function (el) {
          el.hidden = true;
          el.setAttribute('aria-hidden', 'true');
        });
      });

      QUANTITY_SELECTORS.forEach(function (selector) {
        var matches;
        try { matches = row.querySelectorAll(selector); } catch (e) { return; }
        matches.forEach(function (el) { el.disabled = true; });
      });
    });
  }

  function refresh(ctx) {
    if (ctx.busy) return;
    ctx.busy = true;
    ctx.lastFetch = now();

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) {
        if (cart) {
          ctx.items = customizedLines(cart);
          decorate(ctx);
          syncSurchargeQuantities(ctx, cart);
          lockSurchargeRows(cart);
        }
      })
      .catch(function (error) {
        warn('Could not read the cart; leaving it untouched.', error);
      })
      .then(function () { ctx.busy = false; });
  }

  function now() {
    // Date.now via the constructor keeps this readable under the same lint
    // rules as the rest of the extension.
    return new Date().getTime();
  }

  // ── Decoration ────────────────────────────────────────────────────────────

  function decorate(ctx) {
    // Before the early return: a cart whose last customized item was just
    // removed still has our notice sitting above the checkout button.
    renderPrintNote(ctx);

    if (!ctx.items.length) return;

    // Two lines can legitimately carry the same preview (same design added
    // twice before the lock), so work per distinct URL and top up to the count
    // the cart actually reports rather than assuming one each.
    var wanted = {};
    var labels = {};

    var want = function (url, label) {
      if (!url) return;
      wanted[url] = (wanted[url] || 0) + 1;

      // A side name only earns a label when there are two pictures to tell
      // apart. A single-side line stays exactly as it looks today.
      //
      // Cards are placed per URL, so a URL that two lines disagree about gets no
      // label at all: an unlabelled card is merely plainer, while one labelled
      // "Front" over the back of a garment is a lie the customer would act on.
      if (labels.hasOwnProperty(url) && labels[url] !== label) {
        labels[url] = null;
      } else if (!labels.hasOwnProperty(url)) {
        labels[url] = label || null;
      }
    };

    for (var i = 0; i < ctx.items.length; i++) {
      var item = ctx.items[i];
      var twoSided = !!item.secondPreview;

      want(item.preview, twoSided ? sideLabel(item.primarySide) : null);
      want(item.secondPreview, twoSided ? sideLabel(item.secondSide) : null);

      // The artwork properties exist so the design reaches the order and the
      // print team's Asana task — not so the customer reads raw URLs under
      // their cart item. They stay on the line; they just don't render.
      if (item.artwork) hideProperty(item.artwork);
      if (item.secondArtwork) hideProperty(item.secondArtwork);
    }

    var missing = 0;

    Object.keys(wanted).forEach(function (url) {
      var have = cardsFor(url).length;
      for (var n = have; n < wanted[url]; n++) {
        if (!decorateOne(ctx, url, labels[url])) { missing++; break; }
      }
    });

    // Said once, not every observer tick. The design still reaches the order —
    // properties are on the line whether or not the theme prints them — so this
    // is a cosmetic degradation and the wording should not imply otherwise.
    if (missing && !ctx.warned) {
      ctx.warned = true;
      warn(
        missing + ' customized line(s) could not be marked up: this theme does not render ' +
        'line item properties in the cart, so there is nothing to anchor to. The design is ' +
        'still attached to the line and still reaches the order.'
      );
    }
  }

  // ── Print-fidelity notice ─────────────────────────────────────────────────

  /**
   * A short note above the checkout button, for carts holding a customized item.
   *
   * The cart is the last screen we can actually design: checkout UI extensions
   * on the checkout steps are Shopify Plus only, so a styled callout there is
   * not available to these stores. A visible line item property WOULD reach
   * checkout, but only as a labelled row of text under the item.
   *
   * Re-run on every tick, including the ticks our own insertion triggers, so it
   * has to be idempotent — achieved here by clearing every note and rebuilding
   * fresh each time, rather than inserting only where one isn't already
   * present. The simpler "insert if missing" version used to leave a stray
   * note behind whenever noteAnchor()'s answer changed between ticks for the
   * SAME checkout button — which it does in practice: a cart page with
   * accelerated checkout enabled starts with exactly one child in
   * `.cart__ctas` (so the button itself is the anchor), then Shopify injects
   * its own Shop Pay/PayPal buttons into that same row moments later,
   * changing it to a multi-child row (so the anchor becomes the row). The
   * note from the first anchor was never at the row's position, so the
   * second pass's "already announced in this spot" check never saw it and
   * added a second note instead of replacing the first. A full clear removes
   * that whole class of stale-anchor duplicates regardless of why the DOM
   * reshuffled.
   */
  function renderPrintNote(ctx) {
    removePrintNotes();

    if (!ctx.printNote || !ctx.items.length) return;

    checkoutButtons().forEach(function (button) {
      var anchor = noteAnchor(button);
      if (!anchor || !anchor.parentNode) return;

      anchor.parentNode.insertBefore(buildPrintNote(ctx), anchor);
    });
  }

  function removePrintNotes() {
    var notes = document.querySelectorAll('.' + NOTE_CLASS);
    for (var i = 0; i < notes.length; i++) {
      if (notes[i].parentNode) notes[i].parentNode.removeChild(notes[i]);
    }
  }

  // Every checkout control on the page, deduplicated. A theme can legitimately
  // render two — the drawer's and the cart page's — and a closed drawer still
  // needs the notice so it is there when the customer opens it.
  function checkoutButtons() {
    var found = [];

    CHECKOUT_SELECTORS.forEach(function (selector) {
      var matches;
      try { matches = document.querySelectorAll(selector); } catch (e) { return; }

      for (var i = 0; i < matches.length; i++) {
        var el = matches[i];
        if (found.indexOf(el) !== -1) continue;
        // A second selector matching the same region — e.g. a themed class on
        // the same button that [name="checkout"] already found.
        if (found.some(function (seen) { return seen.contains(el) || el.contains(seen); })) continue;
        found.push(el);
      }
    });

    return found;
  }

  /**
   * What to insert the notice in front of.
   *
   * The button itself is usually inside a row of controls (Continue shopping /
   * Check out, or a payment button beside it). Dropping a block-level notice
   * into that row would stretch the row or squash its buttons, so when the
   * parent looks like a layout row we go above the whole row instead.
   */
  function noteAnchor(button) {
    var parent = button.parentElement;
    if (!parent) return button;

    var display = '';
    try { display = window.getComputedStyle(parent).display; } catch (e) { display = ''; }

    var isRow = display === 'flex' || display === 'inline-flex' ||
                display === 'grid' || display === 'inline-grid';

    if (isRow && parent.children.length > 1 && parent.parentNode) return parent;
    return button;
  }

  function buildPrintNote(ctx) {
    var note = document.createElement('p');
    note.className = NOTE_CLASS;
    // Announced politely rather than assertively: it is context for a decision,
    // not an error, and it appears while the customer is reading the cart.
    note.setAttribute('role', 'note');
    note.textContent = ctx.printNote;
    return note;
  }

  // Remove a rendered property line entirely, leaving the underlying line item
  // property untouched. Idempotent by construction: once the text is gone
  // there is nothing left to find on the next pass.
  function hideProperty(url) {
    var node = findUrlTextNode(url);
    if (!node) return;

    var host = node.parentNode;
    if (host && text(host).length <= url.length + LINE_SLACK) {
      var line = climbToPropertyLine(host, url);
      if (line && line.parentNode) {
        removeOrphanedLabel(line);
        line.parentNode.removeChild(line);
        return;
      }
    }

    // Shared text blob — drop the URL and leave the customer's other options.
    node.nodeValue = stripLabelledUrl(node.nodeValue, url);
  }

  // climbToPropertyLine refuses to widen when the parent holds other properties,
  // which is correct — but it means that on themes rendering the label and the
  // URL as separate siblings we removed the URL and left "Design Artwork:"
  // pointing at nothing. Customers saw a bare label with no value.
  //
  // Guarded to ONE adjacent sibling that is a label by shape, so a neighbouring
  // property is never taken with it.
  function removeOrphanedLabel(line) {
    var previous = line.previousElementSibling;
    if (!previous || !isLabel(previous)) return;
    if (previous.parentNode) previous.parentNode.removeChild(previous);
  }

  // Same orphan, one level down: the label and the URL share a text node, so
  // "Design Artwork: <url>" left "Design Artwork: " behind.
  //
  // Walks back from the URL over a short label ending in a colon, bounded by the
  // nearest line break or the previous property's colon so a neighbouring
  // property's value can never be swallowed. Anything it isn't sure about it
  // leaves alone, and only the URL comes out.
  function stripLabelledUrl(value, url) {
    var at = value.indexOf(url);
    if (at === -1) return value;

    var before = value.slice(0, at);
    var after  = value.slice(at + url.length);
    var colon  = before.lastIndexOf(':');

    // The colon must be what introduces THIS url — i.e. nothing but whitespace
    // between them.
    if (colon !== -1 && before.slice(colon + 1).trim() === '') {
      var boundary = Math.max(
        before.lastIndexOf('\n', colon),
        colon > 0 ? before.lastIndexOf(':', colon - 1) : -1
      );
      var label = before.slice(boundary + 1, colon);
      if (label.length <= LABEL_MAX) before = before.slice(0, boundary + 1);
    }

    return before + after;
  }

  function cardsFor(url) {
    var found = [];
    var cards = document.querySelectorAll('.' + CARD_CLASS);
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute(PREVIEW_ATTR) === url) found.push(cards[i]);
    }
    return found;
  }

  function decorateOne(ctx, url, label) {
    var node = findUrlTextNode(url);
    if (!node) return false;

    insertCard(node, url, buildCard(ctx, url, label));
    return true;
  }

  // "left_sleeve" → "Left Sleeve". The side names come from the order contract,
  // so they are machine-shaped; the customer should read English.
  function sideLabel(side) {
    if (!side) return null;
    return String(side)
      .split('_')
      .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
      .join(' ');
  }

  // The theme has already printed the preview URL as the value of the visible
  // `Design Preview` property. Matching on that string rather than on a line
  // index is what makes this safe: if the URL is here, this IS the right line.
  // Index arithmetic would silently put one customer's artwork on another
  // line the moment a theme renders rows in a different order.
  function findUrlTextNode(url) {
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
    var node;

    while ((node = walker.nextNode())) {
      if (!node.nodeValue || node.nodeValue.indexOf(url) === -1) continue;

      var parent = node.parentNode;
      if (!parent || parent.nodeType !== 1) continue;

      var tag = parent.nodeName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA' || tag === 'NOSCRIPT') continue;
      if (parent.closest && parent.closest('.' + CARD_CLASS)) continue; // our own markup

      return node;
    }
    return null;
  }

  function insertCard(node, url, card) {
    var host = node.parentNode;

    // Preferred: replace the whole "Design Preview: <url>" line, so the raw URL
    // disappears instead of sitting next to the thumbnail saying the same thing.
    if (host && text(host).length <= url.length + LINE_SLACK) {
      var line = climbToPropertyLine(host, url);
      if (line && line.parentNode) {
        line.parentNode.replaceChild(card, line);
        return;
      }
    }

    // Fallback for themes that render every property into one text blob:
    // swap out just the URL and leave the surrounding text alone. Replacing the
    // element here would take the customer's other options with it.
    var index = node.nodeValue.indexOf(url);
    var urlNode = node.splitText(index);
    urlNode.splitText(url.length);
    urlNode.parentNode.replaceChild(card, urlNode);
  }

  // Widen from the element holding the URL to the one holding "Design Preview:"
  // AND the URL — Dawn's `<div class="product-option"><dt>…</dt><dd>…</dd></div>`
  // — so the stale label goes with it.
  //
  // Exactly one hop, heavily guarded. Climbing greedily by text length looks
  // fine on a cart with one property and quietly eats the neighbouring one as
  // soon as there are two: a <dl> holding "Design: ok123" and "Design Preview:
  // <url>" is still short enough to pass a naive budget check, and replacing it
  // would delete the design handle the print team quotes.
  function climbToPropertyLine(host, url) {
    var parent = host.parentNode;
    if (!parent || parent.nodeType !== 1) return host;

    // Multi-property containers by definition. Never replace one; we would take
    // every other property on the line with it.
    var tag = parent.nodeName;
    if (tag === 'DL' || tag === 'UL' || tag === 'OL' || tag === 'BODY') return host;

    // Anything beyond the label is somebody else's content.
    if (text(parent).length - url.length > LINE_SLACK) return host;

    var kids = parent.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i] === host) continue;
      if (text(kids[i]).length && !isLabel(kids[i])) return host;
    }
    return parent;
  }

  // A sibling that is just this property's name. Tag first, then the shape every
  // theme falls back to — short text ending in a colon.
  function isLabel(element) {
    var tag = element.nodeName;
    if (tag === 'DT' || tag === 'LABEL' || tag === 'STRONG' || tag === 'B') return true;

    var content = text(element);
    return content.length <= LABEL_MAX && content.charAt(content.length - 1) === ':';
  }

  function text(element) {
    return (element.textContent || '').trim();
  }

  // ── The card ──────────────────────────────────────────────────────────────

  function buildCard(ctx, url, label) {
    var card = document.createElement('div');
    card.className = CARD_CLASS;
    card.setAttribute(PREVIEW_ATTR, url);

    if (ctx.showThumbnail) card.appendChild(buildThumb(ctx, url, label));

    var meta = document.createElement('div');
    meta.className = 'trucustom-cart__meta';

    // Which side this picture is of. Only present on a two-sided line, where
    // two otherwise identical cards would be impossible to tell apart.
    if (label) {
      var side = document.createElement('span');
      side.className = 'trucustom-cart__side';
      side.textContent = label;
      meta.appendChild(side);
    }

    var badge = document.createElement('span');
    badge.className = 'trucustom-cart__badge';
    badge.textContent = ctx.badgeLabel;
    meta.appendChild(badge);

    // type="button" is load-bearing: the cart page wraps its line items in a
    // form, and a default-type button inside it submits the cart on click.
    var view = document.createElement('button');
    view.type = 'button';
    view.className = 'trucustom-cart__view';
    view.textContent = ctx.viewLabel;
    view.addEventListener('click', function () { openLightbox(ctx, url); });
    meta.appendChild(view);

    card.appendChild(meta);
    return card;
  }

  function buildThumb(ctx, url, label) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'trucustom-cart__thumb';
    // Two thumbnails on one line need distinguishable labels for anyone using a
    // screen reader, who otherwise hears "View design" twice with no way to
    // tell which side each opens.
    button.setAttribute('aria-label', label ? ctx.viewLabel + ' — ' + label : ctx.viewLabel);

    var image = document.createElement('img');
    image.src = url;
    image.alt = label ? ctx.thumbAlt + ' — ' + label : ctx.thumbAlt;
    image.loading = 'lazy';
    // A dead preview must not leave an empty grey box in the cart. Drop the
    // thumbnail and keep the badge — the line is still customized either way.
    image.addEventListener('error', function () {
      if (button.parentNode) button.parentNode.removeChild(button);
    });

    button.appendChild(image);
    button.addEventListener('click', function () { openLightbox(ctx, url); });
    return button;
  }

  // ── Lightbox ──────────────────────────────────────────────────────────────
  // The design is cart-locked by the time it reaches here, so it is immutable —
  // a still of the saved preview is the whole truth, no editor needed.

  function openLightbox(ctx, url) {
    closeLightbox(ctx);

    var overlay = document.createElement('div');
    overlay.className = 'trucustom-cart__overlay';

    var dialog = document.createElement('div');
    dialog.className = 'trucustom-cart__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', ctx.thumbAlt);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'trucustom-cart__close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', function () { closeLightbox(ctx); });

    var image = document.createElement('img');
    image.className = 'trucustom-cart__full';
    image.src = url;
    image.alt = ctx.thumbAlt;

    dialog.appendChild(close);
    dialog.appendChild(image);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeLightbox(ctx);
    });

    ctx.lightbox = overlay;
    ctx.onKey = function (event) {
      if (event.key === 'Escape' || event.keyCode === 27) closeLightbox(ctx);
    };
    document.addEventListener('keydown', ctx.onKey);
    close.focus();
  }

  function closeLightbox(ctx) {
    if (ctx.onKey) {
      document.removeEventListener('keydown', ctx.onKey);
      ctx.onKey = null;
    }
    if (ctx.lightbox && ctx.lightbox.parentNode) {
      ctx.lightbox.parentNode.removeChild(ctx.lightbox);
    }
    ctx.lightbox = null;
  }

  // ── Staying applied ───────────────────────────────────────────────────────

  function watch(ctx) {
    var timer = null;

    function schedule() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = null;
        tick(ctx);
      }, 300);
    }

    // Cart drawers, quantity steppers and section re-renders all replace the
    // markup we decorated, and every theme signals that differently — or not at
    // all. Watching the DOM covers the ones that stay silent.
    try {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    } catch (error) {
      warn('Could not observe cart changes; decoration will not survive a re-render.', error);
    }

    document.addEventListener('trucustom:cart-updated', schedule); // ours, from trucustom.js
    // Shopify's own Standard Events (https://cdn.shopify.com/storefront/standard-events.js,
    // imported as @shopify/events by current themes including this one) — dispatched by the
    // theme's own quantity stepper, cart-items-component and product-form.js on every real
    // cart change. This is the one signal guaranteed to fire even when a theme's own
    // re-render is too surgical (a morphed text node, no added/removed elements) for the
    // MutationObserver above to notice — exactly what was silently missing the print-type
    // surcharge's quantity staying in sync with its product line on a quantity-stepper change.
    document.addEventListener('shopify:cart:lines-update', schedule);
    ['cart:refresh', 'cart:updated', 'cart-drawer:open'].forEach(function (name) {
      document.addEventListener(name, schedule);
    });
  }

  function tick(ctx) {
    // Re-applying from the cached cart is free, so do that on every tick and
    // only go back to the network when enough time has passed for the cart to
    // have genuinely changed under us.
    decorate(ctx);

    var elapsed = now() - ctx.lastFetch;
    if (elapsed >= MIN_FETCH_INTERVAL) {
      refresh(ctx);
      return;
    }

    // Throttled: without this, a change landing inside the throttle window
    // (e.g. removing the main product right after some other cart activity)
    // gets silently dropped — decorate() ran above, but refresh() is what
    // actually re-fetches /cart.js and calls syncSurchargeQuantities(), so
    // the orphaned surcharge line this change should have removed just sits
    // there until something ELSE happens to tick again after the throttle
    // window has passed on its own. Guarantee a trailing refresh once the
    // window reopens instead, unless one is already queued.
    if (ctx.trailingTick) return;
    ctx.trailingTick = window.setTimeout(function () {
      ctx.trailingTick = null;
      refresh(ctx);
    }, MIN_FETCH_INTERVAL - elapsed);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
